/* Newsletter signup section
 * Updated: Redesigned to match the Buitn Planten visual system — dark antraciet
 * background, rounded-[5px] corners, white action button, consistent typography
 * and spacing with the rest of the site.
 * Security: duplicate subscriber addresses return the same response as new ones.
 */

import { motion } from 'framer-motion';
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { CircleCheck as CheckCircle, Loader as Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { trackGoal } from '@/lib/datafast';

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || name.trim().length === 0) {
      toast.error('Voer je naam in');
      return;
    }

    if (!email || !email.includes('@')) {
      toast.error('Voer een geldig e-mailadres in');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert({
          email: email.toLowerCase().trim(),
          name: name.trim(),
        });

      if (error && error.code !== '23505') {
        throw error;
      }

      setIsSuccess(true);
      trackGoal('newsletter_subscribed');
      toast.success('Bedankt! Je inschrijving is verwerkt.');
      setEmail('');
      setName('');

      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error('Er is iets misgegaan. Probeer het later opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="nieuwsbrief" data-fast-scroll="viewed_newsletter" className="relative overflow-hidden bg-[#1e2b22] py-20">
      {/* Subtle laurier overlay */}
      <div
        className="absolute inset-0 z-0 opacity-[0.06]"
        style={{
          backgroundImage: 'url(/assets/laurier-black.webp)',
          backgroundSize: '45%',
          backgroundPosition: '100% 50%',
          backgroundRepeat: 'no-repeat',
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1240px] px-5 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          {/* Left — Content & Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex flex-col justify-center"
          >
            <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">
              Blijf op de hoogte
            </p>
            <h2 className="mb-6 text-4xl text-[#e7e1d6] md:text-5xl">
              Nieuwsbrief
            </h2>
            <p className="mb-3 max-w-[460px] text-base leading-relaxed text-[#cfcac2]">
              Blijf op de hoogte van ons nieuwste aanbod, seizoensacties en
              professionele tuintips. Schrijf je in en ontvang exclusieve
              kortingen en advies over het onderhoud van je haagplanten.
            </p>
            <p className="mb-8 max-w-[460px] text-sm leading-relaxed text-[#5a6552]">
              Geen spam, alleen waardevolle informatie over tuinplanten,
              plantenverzorging en speciale aanbiedingen. Je kunt je op elk
              moment weer uitschrijven.
            </p>

            {/* Newsletter Form */}
            <motion.form
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="max-w-[460px] space-y-3"
            >
              {isSuccess ? (
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex items-center gap-3 rounded-[5px] border border-[#5a6552] bg-[#1e2b22] px-4 py-4"
                >
                  <CheckCircle className="h-5 w-5 flex-shrink-0 text-[#5a6552]" />
                  <p className="text-sm font-medium text-[#e7e1d6]">
                    Je bent succesvol ingeschreven!
                  </p>
                </motion.div>
              ) : (
                <>
                  <Input
                    type="text"
                    placeholder="Naam"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="h-12 rounded-[5px] border-[#5a6552] bg-[#1e2b22] text-[#e7e1d6] placeholder:text-[#5a6552]/60 transition-colors focus:border-[#5a6552] focus-visible:ring-[#5a6552]"
                  />
                  <Input
                    type="email"
                    placeholder="Jouw e-mailadres"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="h-12 rounded-[5px] border-[#5a6552] bg-[#1e2b22] text-[#e7e1d6] placeholder:text-[#5a6552]/60 transition-colors focus:border-[#5a6552] focus-visible:ring-[#5a6552]"
                  />
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="group inline-flex h-12 w-full items-center justify-center gap-3 rounded-md bg-white px-6 text-sm font-semibold text-[#1e2b22] shadow-[0_10px_30px_rgba(0,0,0,0.18)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#e7e1d6] hover:shadow-[0_14px_34px_rgba(0,0,0,0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1e2b22]"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Inschrijven...
                      </>
                    ) : (
                      <>
                        Schrijf je in
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </Button>
                  <p className="pt-1 text-center text-xs text-[#5a6552]">
                    Door je in te schrijven ga je akkoord met onze privacyverklaring
                  </p>
                </>
              )}
            </motion.form>
          </motion.div>

          {/* Right — Image */}
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative hidden h-[400px] overflow-hidden rounded-[5px] lg:block"
          >
            <img
              src="/assets/sections/newsletter-section.webp"
              alt="Blijf op de hoogte met onze nieuwsbrief — Buitn Planten"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#1e2b22]/30 via-transparent to-transparent" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
